import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import Styles from './EnergyChartCard.module.css'
import { Colors } from 'Theme'
import PropTypes from 'prop-types'
import { ChartComponent, IFText, IFSkeleton } from 'Components'
import AnalyticsSelectors from 'Stores/Analytics/Selectors'
import AnalyticsActions from 'Stores/Analytics/Actions'
import RequestState from 'Enums/RequestState'
import { useTranslation } from 'react-i18next'
import { useApexResizeObserver } from 'Utils/Resizeobserver'

const EnergyChartCard = ({
  energyChart,
  fetchEnergyChart,
  fetchEnergyChartRequestState,
  filter,
}) => {
  const chartWrapperRef = useApexResizeObserver()
  const { t } = useTranslation()

  useEffect(() => {
    fetchEnergyChart(filter)
  }, [])

  useEffect(() => {
    fetchEnergyChart(filter)
  }, [filter])

  const { series, options, hasNoData } = useMemo(() => {
    const labels = energyChart?.data?.labels || []
    const acData = energyChart?.data?.intervalsAC || []
    const dcData = energyChart?.data?.intervalsDC || []

    const series = [
      {
        name: t('Analytics.Ac'),
        data: acData,
      },
      {
        name: t('Analytics.Dc'),
        data: dcData,
      },
    ]

    const hasNoData = series.every(
      (s) =>
        !s.data ||
        s.data.length === 0 ||
        s.data.every((val) => Number(val) === 0),
    )

    const options = {
      chart: {
        type: 'bar',
        stacked: false,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          columnWidth: '45%',
          borderRadius: 4,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: false,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: labels.map((date) =>
          new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
        ),
        labels: {
          style: {
            colors: Colors.grey500,
          },
        },
      },
      yaxis: {
        labels: {
          formatter: (val) => Math.round(val),
        },
        title: {
          text: t('Analytics.Energy'),
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${Math.round(val)} kWh`,
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
      },
      colors: [Colors.primary, Colors.Analytics.Icons],
    }

    return { series, options, hasNoData }
  }, [energyChart])

  return (
    <div style={{ backgroundColor: Colors.white }} className={Styles.Card}>
      <div className={Styles.GraphContainer}>
        {fetchEnergyChartRequestState === RequestState.LOADING ? (
          <div className={Styles.ChartSkeleton}>
            <IFSkeleton width="100%" height="100%" />
          </div>
        ) : (
          <div ref={chartWrapperRef} className={Styles.Chart}>
            <ChartComponent
              options={options}
              series={series}
              height="100%"
              width="100%"
              type="bar"
              className={
                hasNoData &&
                !(
                  fetchEnergyChartRequestState === RequestState.LOADING ||
                  fetchEnergyChartRequestState === RequestState.SUCCEEDED ||
                  fetchEnergyChartRequestState == RequestState.UNINITIALIZED
                ) &&
                Styles.OverlayTest
              }
            />

            {hasNoData &&
              !(
                fetchEnergyChartRequestState === RequestState.LOADING ||
                fetchEnergyChartRequestState === RequestState.SUCCEEDED ||
                fetchEnergyChartRequestState == RequestState.UNINITIALIZED
              ) && (
                <IFText
                  className={Styles.OverlayText}
                  style={{ color: Colors.red }}
                >
                  {t('Analytics.NoData')}
                </IFText>
              )}
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  energyChart: AnalyticsSelectors.getEnergyChart(state),
  fetchEnergyChartRequestState:
    AnalyticsSelectors.getEnergyChartRequestState(state),
})

function mapDispatchToProps(dispatch) {
  return {
    fetchEnergyChart: (filter) =>
      dispatch(AnalyticsActions.fetchEnergyChart(filter)),
  }
}

EnergyChartCard.propTypes = {
  filter: PropTypes.object,
  fetchEnergyChart: PropTypes.func,
  fetchEnergyChartRequestState: PropTypes.number,
  energyChart: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(EnergyChartCard)
